import React, { useState } from 'react';
import { KnowledgeBaseFolder, knowledgeBaseFolderApi } from '../../api/knowledge-base';
import { XIcon } from 'lucide-react';
import { VisaTypeSelector } from './VisaTypeSelector';

interface FolderManagerProps {
  folder?: KnowledgeBaseFolder;
  parentFolder?: KnowledgeBaseFolder | null;
  onClose: () => void;
  onSaved: () => void;
}

export const FolderManager: React.FC<FolderManagerProps> = ({
  folder,
  parentFolder,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(folder?.name || '');
  const [description, setDescription] = useState(folder?.description || '');
  const [visaTypes, setVisaTypes] = useState<string[]>(folder?.visaTypes || []);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      setLoading(true);

      const data = {
        name,
        description: description || undefined,
        visaTypes,
        parentId: parentFolder?.id,
      };

      if (folder) {
        await knowledgeBaseFolderApi.update(folder.id, data);
      } else {
        await knowledgeBaseFolderApi.create(data);
      }

      onSaved();
    } catch (error) {
      console.error('Failed to save folder:', error);
      alert('Failed to save folder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {folder ? 'Edit Folder' : 'Create Folder'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Field Definitions"
            />
          </div>

          {/* Parent Folder (read-only display) */}
          {parentFolder && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Folder</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
                {parentFolder.path}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the purpose of this folder..."
            />
          </div>

          {/* Visa Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Applicable Visa Types
            </label>
            <VisaTypeSelector value={visaTypes} onChange={setVisaTypes} />
            <p className="mt-1 text-xs text-gray-500">
              Documents in this folder will inherit these visa types by default
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Folder'}
          </button>
        </div>
      </div>
    </div>
  );
};
